import { PrintType } from '@/features/print/constants'
import { useCallback, useEffect, useMemo, useRef } from 'react'

type PrintTypeOnChangeCallbacks<T extends readonly PrintType[]> = Partial<
    Record<PrintType, (newChecked: boolean) => void>
> &
    Record<T[number], (newChecked: boolean) => void>

export const usePrintTypeOnChangeCallbacks = <const T extends readonly PrintType[]>(
    printTypes: T,
    fieldValue: T[number][],
    fieldOnChange: (newValue: T[number][]) => void
): PrintTypeOnChangeCallbacks<T> => {
    const fieldValueRef = useRef(fieldValue)

    useEffect(() => {
        fieldValueRef.current = fieldValue
    }, [fieldValue])

    const handleChange = useCallback(
        (printType: PrintType, newChecked: boolean) => {
            const currentValue = fieldValueRef.current
            const newValue = newChecked
                ? [...currentValue, printType]
                : currentValue.filter((type) => type !== printType)
            fieldOnChange(newValue)
        },
        [fieldOnChange]
    )

    return useMemo(() => {
        return printTypes.reduce(
            (callbacks, printType) => {
                callbacks[printType] = (newChecked: boolean) => handleChange(printType, newChecked)

                return callbacks
            },
            {} as Partial<PrintTypeOnChangeCallbacks<T>>
        ) as PrintTypeOnChangeCallbacks<T>
    }, [handleChange, printTypes])
}
